import { useParams, Link } from "react-router-dom";
import {
 Container, Section, Breadcrumbs, BtnPrimary, CallBtn, ScheduleBtn, MapEmbed, Loader,
 PHONE, ShieldCheck, CheckCircle2, Phone, ArrowRight, Clock,
} from "../components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useService, useServices } from "../lib/api";
import { useSeo, breadcrumbLd, faqLd } from "../lib/seo";

export default function ServiceDetail() {
 const { slug } = useParams();
 const { data: svc, isLoading, isError } = useService(slug);
 const { data: all = [] } = useServices();

 useSeo({
 title: svc?.metaTitle,
 description: svc?.metaDescription,
 path: `/services/${slug}`,
 image: svc?.image,
 jsonLd: svc ? [
 { "@context": "https://schema.org", "@type": "Service", name: svc.name, serviceType: svc.name,
 description: svc.metaDescription, areaServed: "Riverside County, CA",
 provider: { "@type": "AutoRepair", name: "RPM Auto Repair", telephone: "+19516534777" } },
 faqLd(svc.faqs),
 breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: svc.name, path: `/services/${slug}` }]),
 ] : [],
 });

 if (isLoading) return <Loader />;
 if (isError || !svc) return <NotFoundInline />;

 const related = all.filter((s) => svc.related?.includes(s.name)).slice(0, 4);

 return (
 <>
 <section className="relative overflow-hidden border-b border-white/10">
 <img src={svc.image} alt={`${svc.name} in Riverside`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
 <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
 <Container className="relative py-16 sm:py-20">
 <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: svc.name, path: `/services/${slug}` }]} />
 <span className="inline-block rounded-full bg-primary/90 px-3 py-1 text-xs font-bold tracking-wider text-white">{svc.category}</span>
 <h1 className="mt-4 max-w-4xl font-display text-4xl font-700 leading-[1.05] text-white sm:text-5xl lg:text-6xl">{svc.name} in Riverside, CA</h1>
 <p className="mt-5 max-w-2xl text-lg text-white/75">{svc.short}. Honest, ASE Master Certified service backed by a nationwide warranty.</p>
 <div className="mt-8 flex flex-col gap-4 sm:flex-row">
 <ScheduleBtn label="Book This Service" testid="service-schedule" />
 <CallBtn />
 </div>
 </Container>
 </section>

 <Section>
 <Container className="grid gap-12 lg:grid-cols-[1fr_340px]">
 <article className="prose-rpm max-w-none" data-testid="service-content" dangerouslySetInnerHTML={{ __html: svc.body }} />

 <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
 <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-[#1a1013] to-steel p-6">
 <h3 className="font-display text-xl text-white">Get a Free Estimate</h3>
 <p className="mt-2 text-sm text-muted-foreground">No pressure, no surprises. We'll inspect your vehicle and give you an honest written quote.</p>
 <div className="mt-5 space-y-3">
 <ScheduleBtn className="w-full" label="Schedule Now" />
 <a href={`tel:+19516534777`} className="flex w-full items-center justify-center gap-2 rounded-md border border-white/20 py-3 text-sm font-semibold text-white hover:border-primary">
 <Phone size={15} className="text-primary" /> {PHONE}
 </a>
 </div>
 <ul className="mt-6 space-y-2.5 text-sm text-muted-foreground">
 {["ASE Master Certified", "Nationwide 24k-mile warranty", "Financing available", "All makes & models"].map((x) => (
 <li key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> {x}</li>
 ))}
 </ul>
 </div>

 {related.length > 0 && (
 <div className="rounded-xl border border-white/10 bg-steel p-6">
 <h3 className="font-display text-lg text-white">Related Services</h3>
 <ul className="mt-4 space-y-2">
 {related.map((r) => (
 <li key={r.slug}>
 <Link to={`/services/${r.slug}`} className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary">
 {r.name} <ArrowRight size={14} />
 </Link>
 </li>
 ))}
 </ul>
 </div>
 )}

 <div className="rounded-xl border border-white/10 bg-steel p-6">
 <h3 className="flex items-center gap-2 font-display text-lg text-white"><Clock size={16} className="text-primary" /> Hours</h3>
 <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
 <li className="flex justify-between"><span>Mon-Fri</span><span>8:00-6:00</span></li>
 <li className="flex justify-between"><span>Saturday</span><span>8:00-2:00</span></li>
 <li className="flex justify-between"><span>Sunday</span><span>Closed</span></li>
 </ul>
 </div>
 </aside>
 </Container>
 </Section>

 {/* FAQ */}
 <Section className="bg-steel/40">
 <Container className="max-w-4xl">
 <h2 className="mb-8 font-display text-3xl font-600 text-white sm:text-4xl">{svc.name} FAQs</h2>
 <Accordion type="single" collapsible className="space-y-3" data-testid="service-faqs">
 {svc.faqs.map((f, i) => (
 <AccordionItem key={i} value={`f${i}`} className="rounded-xl border border-white/10 bg-steel px-5">
 <AccordionTrigger className="text-left font-display text-base text-white hover:no-underline">{f.q}</AccordionTrigger>
 <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
 </AccordionItem>
 ))}
 </Accordion>
 </Container>
 </Section>

 <Section>
 <Container className="grid items-center gap-8 rounded-2xl border border-white/10 bg-steel p-8 lg:grid-cols-2">
 <div>
 <ShieldCheck className="text-primary" size={32} />
 <h2 className="mt-3 font-display text-2xl text-white">Serving All of Riverside County</h2>
 <p className="mt-2 text-sm text-muted-foreground">Stop by or schedule online. We proudly serve Riverside, Corona, Moreno Valley, Jurupa Valley, Norco and beyond.</p>
 <div className="mt-5"><BtnPrimary to="/schedule-appointment">Schedule Appointment <ArrowRight size={16} /></BtnPrimary></div>
 </div>
 <MapEmbed title={`Map to RPM Auto Repair for ${svc.name}`} />
 </Container>
 </Section>
 </>
 );
}

function NotFoundInline() {
 return (
 <Container className="py-32 text-center">
 <h1 className="font-display text-4xl text-white">Service Not Found</h1>
 <p className="mt-3 text-muted-foreground">The service you're looking for doesn't exist.</p>
 <div className="mt-6 flex justify-center"><BtnPrimary to="/services">View All Services</BtnPrimary></div>
 </Container>
 );
}
